/**
 * SiteSettings - V2 Generator Management Component
 *
 * Allows admins to:
 * - View/switch generator version (v1 ↔ v2)
 * - View/change recipe selection
 * - Toggle fullbleed hero option
 * - Trigger V2 regeneration with preserve options
 * - Preview color changes in real-time
 */

import { useState, useEffect } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';
import {
  getGeneratorVersion,
  setGeneratorVersion,
  getSiteRecipe,
  updateSiteRecipe,
  regenerateWithV2,
  type GeneratorVersion,
  type RecipeId,
  type HeroVariant,
  type RegenerateOptions,
  type UpdateRecipeParams,
} from '../api/site-settings';
import { getStagingState } from '../api/staging';
import { SitePreview, type EditableSiteContent } from '../renderer';

interface SiteSettingsProps {
  websiteId: string;
  onRegenerationQueued?: () => void;
}

const RECIPE_OPTIONS: Array<{ id: RecipeId; name: string; description: string }> = [
  {
    id: 'bold-modern',
    name: 'Bold & Modern',
    description: 'Strong visuals, contemporary design, makes a statement',
  },
  {
    id: 'classic-elegant',
    name: 'Classic & Elegant',
    description: 'Timeless design, refined typography, sophisticated feel',
  },
  {
    id: 'warm-friendly',
    name: 'Warm & Friendly',
    description: 'Inviting colors, approachable tone, personal touch',
  },
  {
    id: 'clean-professional',
    name: 'Clean & Professional',
    description: 'Minimal design, clear information, trustworthy appearance',
  },
];

const HERO_OPTIONS: Array<{ id: HeroVariant; name: string; description: string }> = [
  {
    id: 'standard',
    name: 'Standard',
    description: 'Traditional hero with contained content area',
  },
  {
    id: 'fullbleed',
    name: 'Full Bleed',
    description: 'Edge-to-edge hero image for maximum visual impact',
  },
];

export function SiteSettings({ websiteId, onRegenerationQueued }: SiteSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Current values
  const [generatorVersion, setGeneratorVersionState] = useState<GeneratorVersion>('v1');
  // Gates every legacy-generator control. V4 sites keep their content in the
  // editor, so regenerating replaces rather than refreshes — see below.
  const isV4 = generatorVersion === 'v4';
  const [recipeId, setRecipeIdState] = useState<RecipeId>('clean-professional');
  const [heroVariant, setHeroVariantState] = useState<HeroVariant>('standard');

  // Current colors (saved to server)
  const [primaryColor, setPrimaryColorState] = useState<string>('#3AABE8');
  const [secondaryColor, setSecondaryColorState] = useState<string>('#F9B03F');

  // Local input values (for typing before blur)
  const [primaryColorInput, setPrimaryColorInput] = useState<string>('#3AABE8');
  const [secondaryColorInput, setSecondaryColorInput] = useState<string>('#F9B03F');

  // Pending changes (for save button)
  const [pendingRecipe, setPendingRecipe] = useState<RecipeId | null>(null);
  const [pendingHero, setPendingHero] = useState<HeroVariant | null>(null);
  const [pendingPrimaryColor, setPendingPrimaryColor] = useState<string | null>(null);
  const [pendingSecondaryColor, setPendingSecondaryColor] = useState<string | null>(null);

  // Regeneration options
  // preserveImages defaults to true - stock images are expensive to regenerate
  const [preserveContent, setPreserveContent] = useState(false);
  const [preserveImages, setPreserveImages] = useState(true);

  // Color preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewContent, setPreviewContent] = useState<EditableSiteContent | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, [websiteId]);

  async function fetchSettings() {
    setLoading(true);
    setError(null);
    try {
      const [versionRes, recipeRes] = await Promise.all([
        getGeneratorVersion(websiteId),
        getSiteRecipe(websiteId),
      ]);
      setGeneratorVersionState(versionRes.data.generatorVersion);
      setRecipeIdState(recipeRes.data.recipeId);
      setHeroVariantState(recipeRes.data.heroVariant);
      if (recipeRes.data.primaryColor) {
        setPrimaryColorState(recipeRes.data.primaryColor);
        setPrimaryColorInput(recipeRes.data.primaryColor);
      }
      if (recipeRes.data.secondaryColor) {
        setSecondaryColorState(recipeRes.data.secondaryColor);
        setSecondaryColorInput(recipeRes.data.secondaryColor);
      }
    } catch (err) {
      setError('Failed to load site settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleVersionChange(newVersion: GeneratorVersion) {
    setActionLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await setGeneratorVersion(websiteId, newVersion);
      setGeneratorVersionState(newVersion);
      setSuccessMessage(`Generator version set to ${newVersion.toUpperCase()}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update version');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSaveRecipeChanges() {
    if (!pendingRecipe && !pendingHero && !pendingPrimaryColor && !pendingSecondaryColor) return;

    setActionLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const params: UpdateRecipeParams = {};
      if (pendingRecipe) params.recipeId = pendingRecipe;
      if (pendingHero) params.heroVariant = pendingHero;
      if (pendingPrimaryColor) params.primaryColor = pendingPrimaryColor;
      if (pendingSecondaryColor) params.secondaryColor = pendingSecondaryColor;

      // If no pending changes, still send current values for recipe/hero
      if (!pendingRecipe && !pendingHero && !pendingPrimaryColor && !pendingSecondaryColor) {
        return;
      }

      await updateSiteRecipe(websiteId, params);

      if (pendingRecipe) setRecipeIdState(pendingRecipe);
      if (pendingHero) setHeroVariantState(pendingHero);
      if (pendingPrimaryColor) setPrimaryColorState(pendingPrimaryColor);
      if (pendingSecondaryColor) setSecondaryColorState(pendingSecondaryColor);

      setPendingRecipe(null);
      setPendingHero(null);
      setPendingPrimaryColor(null);
      setPendingSecondaryColor(null);
      setSuccessMessage('Settings saved. Regenerate to apply changes.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRegenerate() {
    setActionLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const options: RegenerateOptions = {
        preserveContent,
        preserveImages,
      };
      const res = await regenerateWithV2(websiteId, options);
      setGeneratorVersionState('v2');
      setSuccessMessage(res.message);
      onRegenerationQueued?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to queue regeneration');
    } finally {
      setActionLoading(false);
    }
  }

  const hasUnsavedChanges = pendingRecipe !== null || pendingHero !== null || pendingPrimaryColor !== null || pendingSecondaryColor !== null;
  const effectiveRecipe = pendingRecipe || recipeId;
  const effectiveHero = pendingHero || heroVariant;
  const effectivePrimaryColor = pendingPrimaryColor || primaryColor;
  const effectiveSecondaryColor = pendingSecondaryColor || secondaryColor;

  async function handleOpenPreview() {
    setPreviewLoading(true);
    try {
      const stagingState = await getStagingState(websiteId);
      if (stagingState.content) {
        setPreviewContent(stagingState.content);
        setShowPreviewModal(true);
      } else {
        setError('No content available for preview. The site may not have been generated yet.');
      }
    } catch (err) {
      setError('Failed to load preview content');
      console.error(err);
    } finally {
      setPreviewLoading(false);
    }
  }

  if (loading) {
    return (
      <Card>
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      {/* Generator Version */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Generator Version</h3>
        {isV4 ? (
          /*
           * V4 sites get a read-only statement, not a switch.
           *
           * V1 and V2 regenerate from onboarding data and replace the site
           * wholesale, while a V4 site's content lives in D1 as rawPages /
           * rawSections. Offering the toggle here meant one click could arm a
           * job that discards every editor change on a live customer site.
           * The server refuses this too — this only stops staff being offered
           * something that will now error.
           */
          <p className="text-sm text-gray-500">
            This site uses <span className="font-medium text-gray-900">V4</span> (programmatic
            scaffold). Its content lives in the editor, not in a regeneration template — switching
            generators would rebuild the site from onboarding data and discard all current content,
            so it is not offered. Use the editor to make changes.
          </p>
        ) : (
        <>
        <p className="text-sm text-gray-500 mb-4">
          V2 uses the new section composition system with style recipes. V1 uses legacy Handlebars templates.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => handleVersionChange('v1')}
            disabled={actionLoading || generatorVersion === 'v1'}
            className={`
              flex-1 px-4 py-3 rounded-lg border-2 transition-all text-center
              ${generatorVersion === 'v1'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }
              ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <div className="font-medium">V1</div>
            <div className="text-xs mt-1 opacity-75">Legacy Templates</div>
          </button>
          <button
            onClick={() => handleVersionChange('v2')}
            disabled={actionLoading || generatorVersion === 'v2'}
            className={`
              flex-1 px-4 py-3 rounded-lg border-2 transition-all text-center
              ${generatorVersion === 'v2'
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 hover:border-gray-300 text-gray-600'
              }
              ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <div className="font-medium">V2</div>
            <div className="text-xs mt-1 opacity-75">Section Composition</div>
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Current: <span className="font-medium">{generatorVersion.toUpperCase()}</span>
        </p>
        </>
        )}
      </Card>

      {/* Style Recipe */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Style Recipe</h3>
        <p className="text-sm text-gray-500 mb-4">
          Choose the visual style for V2-generated sites. Changes require regeneration to take effect.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {RECIPE_OPTIONS.map((recipe) => (
            <button
              key={recipe.id}
              onClick={() => setPendingRecipe(recipe.id === recipeId ? null : recipe.id)}
              disabled={actionLoading}
              className={`
                p-4 rounded-lg border-2 transition-all text-left
                ${effectiveRecipe === recipe.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
                }
                ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <div className={`font-medium ${effectiveRecipe === recipe.id ? 'text-primary-700' : 'text-gray-900'}`}>
                {recipe.name}
              </div>
              <div className="text-xs text-gray-500 mt-1">{recipe.description}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Hero Variant */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Hero Style</h3>
        <p className="text-sm text-gray-500 mb-4">
          Choose how the hero section is displayed. Changes require regeneration to take effect.
        </p>
        <div className="flex gap-3">
          {HERO_OPTIONS.map((hero) => (
            <button
              key={hero.id}
              onClick={() => setPendingHero(hero.id === heroVariant ? null : hero.id)}
              disabled={actionLoading}
              className={`
                flex-1 p-4 rounded-lg border-2 transition-all text-left
                ${effectiveHero === hero.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
                }
                ${actionLoading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <div className={`font-medium ${effectiveHero === hero.id ? 'text-primary-700' : 'text-gray-900'}`}>
                {hero.name}
              </div>
              <div className="text-xs text-gray-500 mt-1">{hero.description}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Brand Colors */}
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Brand Colors</h3>
        <p className="text-sm text-gray-500 mb-4">
          Customize the primary and secondary colors used throughout the site. Changes require regeneration to take effect.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Primary Color */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Primary Color
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={effectivePrimaryColor}
                onChange={(e) => {
                  const val = e.target.value;
                  setPrimaryColorInput(val);
                  setPendingPrimaryColor(val === primaryColor ? null : val);
                }}
                disabled={actionLoading}
                className="h-10 w-14 rounded border border-gray-300 cursor-pointer disabled:opacity-50 flex-shrink-0"
              />
              <input
                type="text"
                value={primaryColorInput.toUpperCase()}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow typing any hex-like value
                  if (/^#?[0-9A-Fa-f]{0,6}$/.test(val.replace('#', ''))) {
                    setPrimaryColorInput(val.startsWith('#') ? val : `#${val}`);
                  }
                }}
                onBlur={() => {
                  // Validate and save on blur
                  const val = primaryColorInput;
                  if (/^#[0-9A-Fa-f]{6}$/i.test(val)) {
                    setPendingPrimaryColor(val === primaryColor ? null : val);
                  } else {
                    // Reset to current effective value if invalid
                    setPrimaryColorInput(effectivePrimaryColor);
                  }
                }}
                disabled={actionLoading}
                placeholder="#3AABE8"
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono disabled:opacity-50"
              />
            </div>
            <p className="text-xs text-gray-500">Used for buttons, links, and accents</p>
          </div>

          {/* Secondary Color */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Secondary Color
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={effectiveSecondaryColor}
                onChange={(e) => {
                  const val = e.target.value;
                  setSecondaryColorInput(val);
                  setPendingSecondaryColor(val === secondaryColor ? null : val);
                }}
                disabled={actionLoading}
                className="h-10 w-14 rounded border border-gray-300 cursor-pointer disabled:opacity-50 flex-shrink-0"
              />
              <input
                type="text"
                value={secondaryColorInput.toUpperCase()}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow typing any hex-like value
                  if (/^#?[0-9A-Fa-f]{0,6}$/.test(val.replace('#', ''))) {
                    setSecondaryColorInput(val.startsWith('#') ? val : `#${val}`);
                  }
                }}
                onBlur={() => {
                  // Validate and save on blur
                  const val = secondaryColorInput;
                  if (/^#[0-9A-Fa-f]{6}$/i.test(val)) {
                    setPendingSecondaryColor(val === secondaryColor ? null : val);
                  } else {
                    // Reset to current effective value if invalid
                    setSecondaryColorInput(effectiveSecondaryColor);
                  }
                }}
                disabled={actionLoading}
                placeholder="#F9B03F"
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono disabled:opacity-50"
              />
            </div>
            <p className="text-xs text-gray-500">Used for highlights and secondary elements</p>
          </div>
        </div>

        {/* Color Preview */}
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-gray-600">Preview</p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenPreview}
              disabled={previewLoading || actionLoading}
            >
              {previewLoading ? 'Loading...' : 'Preview on Site'}
            </Button>
          </div>
          <div className="flex gap-4 items-center">
            <div
              className="h-8 w-24 rounded shadow-sm"
              style={{ backgroundColor: effectivePrimaryColor }}
            />
            <div
              className="h-8 w-24 rounded shadow-sm"
              style={{ backgroundColor: effectiveSecondaryColor }}
            />
            <div className="flex-1">
              <button
                disabled
                className="px-4 py-2 rounded text-white text-sm font-medium"
                style={{ backgroundColor: effectivePrimaryColor }}
              >
                Primary Button
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Save Changes Button */}
      {hasUnsavedChanges && (
        <div className="flex justify-end">
          <Button
            onClick={handleSaveRecipeChanges}
            disabled={actionLoading}
          >
            {actionLoading ? 'Saving...' : 'Save Recipe Changes'}
          </Button>
        </div>
      )}

      {/* Regenerate Action — never offered on V4 sites (see Generator Version above) */}
      {!isV4 && (
      <Card>
        <h3 className="font-semibold text-gray-900 mb-4">Regenerate Site</h3>
        <p className="text-sm text-gray-500 mb-4">
          Queue this site for regeneration with V2. This will apply the current recipe and hero settings.
        </p>

        {/* Preserve Options */}
        <div className="space-y-3 mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-sm font-medium text-gray-700">Regeneration Options</p>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={preserveContent}
              onChange={(e) => setPreserveContent(e.target.checked)}
              disabled={actionLoading}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Preserve Content</span>
              <p className="text-xs text-gray-500">Keep existing text/copy instead of regenerating with Claude</p>
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={preserveImages}
              onChange={(e) => setPreserveImages(e.target.checked)}
              disabled={actionLoading}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Preserve Images</span>
              <p className="text-xs text-gray-500">Keep currently assigned stock images instead of fetching new ones</p>
            </div>
          </label>
        </div>

        <Button
          onClick={handleRegenerate}
          disabled={actionLoading || hasUnsavedChanges}
          variant={hasUnsavedChanges ? 'secondary' : 'primary'}
        >
          {actionLoading ? 'Queuing...' : 'Regenerate with V2'}
        </Button>
        {hasUnsavedChanges && (
          <p className="text-xs text-amber-600 mt-2">
            Save your recipe changes before regenerating.
          </p>
        )}
      </Card>
      )}

      {/* Color Preview Modal */}
      {showPreviewModal && previewContent && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-900 text-white">
            <div className="flex items-center gap-4">
              <h3 className="font-semibold">Color Preview</h3>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-400">Primary:</span>
                <div
                  className="w-6 h-6 rounded border border-white/20"
                  style={{ backgroundColor: effectivePrimaryColor }}
                />
                <span className="font-mono text-xs">{effectivePrimaryColor}</span>
                <span className="text-gray-400 ml-2">Secondary:</span>
                <div
                  className="w-6 h-6 rounded border border-white/20"
                  style={{ backgroundColor: effectiveSecondaryColor }}
                />
                <span className="font-mono text-xs">{effectiveSecondaryColor}</span>
              </div>
            </div>
            <button
              onClick={() => setShowPreviewModal(false)}
              className="p-2 hover:bg-gray-700 rounded transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Preview Content */}
          <div className="flex-1 overflow-auto bg-white">
            <SitePreview
              content={previewContent}
              editMode={false}
              colorOverrides={{
                primary: effectivePrimaryColor,
                secondary: effectiveSecondaryColor,
              }}
            />
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-100 border-t">
            <p className="text-sm text-gray-600">
              This is a preview. Save changes and regenerate to apply colors to the live site.
            </p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
